/**
 * useConfirm — promise-based confirmation dialog.
 *
 * Replaces native window.confirm() (which is unstyled and inconsistently
 * suppressed by browsers) with a themed AlertDialog popup.
 *
 * Usage:
 *   const [confirm, confirmDialog] = useConfirm();
 *   ...
 *   const ok = await confirm({
 *     title: "Delete test case?",
 *     description: "This action cannot be undone.",
 *     confirmLabel: "Delete",
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *   ...
 *   // render {confirmDialog} once anywhere in the component's JSX
 */
import { useState, useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

export function useConfirm() {
  const [state, setState] = useState({ open: false, options: {} });
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    setState({ open: true, options });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = useCallback((result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
  }, []);

  const {
    title = 'Are you sure?',
    description = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
  } = state.options;

  const dialog = (
    <AlertDialog
      open={state.open}
      onOpenChange={(o) => {
        if (!o) close(false);
      }}
    >
      <AlertDialogContent className="bg-[#0B0F1A] border border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {title}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription className="text-slate-400">
              {description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => close(false)}
            className="bg-transparent border border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => close(true)}
            className={
              destructive
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-[#00E599] text-black hover:bg-[#00CC88]'
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [confirm, dialog];
}

export default useConfirm;
